/**
 * When a newly deployed shell is allowed to take the screen. SPEC.md §17.1, §44.
 *
 * `registerType: 'autoUpdate'` installs a new shell on its own, and
 * vite-plugin-pwa's own handler then calls `window.location.reload()` the moment
 * that shell activates — with `skipWaiting` and `clientsClaim`, that is a few
 * seconds after the page loaded, for however long precaching takes.
 *
 * Those few seconds are exactly when somebody is typing their name into the
 * create form or a join code into the landing screen, and a reload throws both
 * away and returns them to the default screen. It reads as the app losing the
 * tap: they do it again, it works the second time, because by then the shell is
 * current. Once per device per deploy — so it is the *first* thing a player does
 * after a release that gets eaten, every time.
 *
 * The comment this replaces said there was "no update prompt to interrupt a hand
 * in progress", and that was true and beside the point: what shipped instead of
 * a prompt was an unannounced reload, which interrupts strictly more than a
 * prompt does. A prompt at least waits to be answered.
 *
 * So the reload is kept and moved. A new shell is worth having — the alternative
 * is a device pinned to an old bundle indefinitely — but nothing about it is
 * urgent enough to spend a player's typing on. `document.hidden` is the moment it
 * costs nothing: they have backgrounded the app or locked the phone, and coming
 * back to a freshly loaded screen is indistinguishable from coming back to one
 * the OS evicted, which is a path the app already has to survive. The snapshot is
 * the source of truth on reconnect, so a live hand is re-read rather than lost.
 * §17.1
 *
 * Deliberately **not** conditioned on whether a session is live. Reloading a
 * hidden page mid-hand is safe for the reason above, and a policy that also had
 * to ask the session store what was happening would put a second opinion about
 * "is this a good moment" in a file whose whole job is to have no opinions.
 */

/**
 * The two things this needs from a document, and no more.
 *
 * Spelled out rather than `Pick<Document, …>`: that pulls in the real
 * `addEventListener`, whose listener parameter a test double cannot satisfy
 * without pretending to handle every event in `DocumentEventMap`.
 */
export interface VisibilityHost {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface DeferredReload {
  /** How to reload. Injected so the policy is testable without a real page. */
  reload: () => void;
  /** Where to read visibility and listen for it. Defaults to `document`. */
  doc?: VisibilityHost;
}

/**
 * Reload as soon as nobody is looking — now, if that is already true.
 *
 * If the page is never hidden again, it is never reloaded, and the old shell
 * runs until the next cold start. That is the deliberate trade: the only thing
 * this app loads late is the QR encoder (`ShareCard`), whose import is already
 * wrapped so a chunk that no longer exists degrades to the join code and the
 * link rather than throwing. Everything else is in the one bundle the page
 * already has.
 */
export function reloadWhenHidden({ reload, doc = document }: DeferredReload): void {
  const apply = (): void => {
    if (doc.visibilityState !== 'hidden') return;
    // Once only: a reload that has been asked for cannot be asked for twice, and
    // a listener left behind would fire again on the next tab switch.
    doc.removeEventListener('visibilitychange', apply);
    reload();
  };

  doc.addEventListener('visibilitychange', apply);
  // The update may well land while the app is already in the background, which
  // is the best case rather than a special one.
  apply();
}

/**
 * How long a running shell may go without asking whether it is still current.
 * SPEC.md §17.1
 *
 * **Six hours, and this is the app's cache window in the only sense that means
 * anything.** Everything else is already bounded or already immutable:
 * `/assets/*` is content-hashed, so a URL's content can never change and a year
 * is free; the shell, `sw.js` and the manifest are served `no-cache` and are
 * revalidated on every request. What is *not* bounded is the service worker
 * itself. It answers navigations out of its precache, so a browser that has one
 * installed does not ask the network what the app is — it asks the worker, and
 * the worker only learns about a new build when something makes it look.
 *
 * `registerSW` makes it look exactly once, at registration. A tab left open —
 * or an installed app never fully closed, which on a phone is the normal state —
 * therefore stays on the shell it started with for as long as it runs, and the
 * user is looking at a build from whenever they last cold-started. That is the
 * "very old version" a hard refresh does not obviously explain, and this is the
 * bound on it.
 *
 * Six rather than one: a check is a conditional GET for a few hundred bytes, so
 * the cost is not the argument either way. What decides it is that a table plays
 * for an evening, and a window shorter than the evening buys nothing — the shell
 * a hand is being played on cannot be replaced under it anyway (see
 * `reloadWhenHidden` above). Six hours means a phone that is used at all during
 * the day has current bytes on disk before the night starts, and the first time
 * it is backgrounded they are the bytes it runs.
 */
export const SHELL_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

export interface ShellPoll {
  /** Asks the browser to re-fetch the worker script. `registration.update()`. */
  update: () => unknown;
  intervalMs?: number;
  /** Injected so the policy is testable without a real clock. */
  schedule?: (run: () => void, ms: number) => unknown;
  /** Injected so the policy is testable without a real navigator. */
  isOnline?: () => boolean;
}

/**
 * Asks, on a timer, whether there is a newer shell. §17.1
 *
 * Finding one changes nothing a player can see: the new worker installs and
 * activates in the background, and `reloadWhenHidden` above still decides when
 * the page is allowed to become it. All this does is make sure the question gets
 * asked by something other than a cold start.
 *
 * Skipped while offline, because `update()` on a dead radio is a rejected
 * promise and a retry six hours later — and the next check after the network
 * comes back is the one that was going to answer it anyway.
 */
export function pollForNewShell({
  update,
  intervalMs = SHELL_CHECK_INTERVAL_MS,
  schedule = (run, ms) => setInterval(run, ms),
  isOnline = () => navigator.onLine,
}: ShellPoll): void {
  schedule(() => {
    if (!isOnline()) return;
    void update();
  }, intervalMs);
}
